import {defineStore, storeToRefs} from 'pinia'
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import {unref, watch} from "vue";
import {FieldToFocusOptions} from "@/utils/common/TopPanelSearchUtils.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import {setCssVar} from "quasar";
import {useThemeStore} from "@/stores/ThemeDataStore.ts";
import {useNamaBreakpoints} from "@/composables/useNamaBreakpoints.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {GridOptions} from "ag-grid-community";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import router from "@/router";
import RouteNames from "@/router/RouteNames.ts";
import SoundEffects from "@/utils/constants/SoundEffects.ts";
import DTOFieldHelpMessage = namaTypes.DTOFieldHelpMessage;
import DTOAIChatMessage = namaTypes.DTOAIChatMessage;

const {isDesktop} = useNamaBreakpoints()

export const useNamaFlagsStore = defineStore('NamaFlagsStore', {
  state: () => ({
    navBarHeight: 0,
    isSideBarOpen: false,
    aiChatId: '',
    aiChatMessages: new Array<DTOAIChatMessage>(),
    darkMode: localStorage.getItem('color-theme') === 'dark' || (!('color-theme' in localStorage)
      && window.matchMedia('(prefers-color-scheme: dark)').matches),
    quadLayout: NamaCommon.getBooleanFromStorageOrDefault('quad-layout', false),
    sidebarMenuMode: NamaCommon.getStoredValue('side-bar-menu-mode', NamaCommon.getSideBarMenuModeOptions().shift()),
    densityMode: localStorage.getItem('density-mode')
      || (NamaCommon.getBooleanFromStorageOrDefault('compact-mode', unref(isDesktop)) ? 'Compact' : 'Normal'),
    horizontalMode: NamaCommon.getBooleanFromStorageOrDefault("horizontal-mode", unref(isDesktop)),
    showToolbarLabels: NamaCommon.getBooleanFromStorageOrDefault("show-toolbar-labels", unref(isDesktop)),
    sidebarFixed: NamaCommon.getBooleanFromStorageOrDefault('sidebar-fixed', false),
    showSidebarMenuIconColors: NamaCommon.getBooleanFromStorageOrDefault('showSidebarMenuIconColors', true),
    gridOptions: NamaCommon.getStoredValue("gridOptions", {}) as Record<(typeof RouteNames.EDIT_VIEW_ROUTE | typeof RouteNames.LIST_VIEW_ROUTE), Partial<GridOptions>>,
    notificationsSound: NamaCommon.getStoredValue('notifications-sound', SoundEffects.Sound1),
    approvalsSound: NamaCommon.getStoredValue('approvals-sound', SoundEffects.Sound1),
    messagesSound: NamaCommon.getStoredValue('messages-sound', SoundEffects.Sound1),
    taskEndedSound: NamaCommon.getStoredValue('task-ended-sound', SoundEffects.Sound1),
    replicationOptionsActive: false,
    serverLogActive: false,
    showFieldInfoActive: false,
    showHelpMessagesActive: false,
    filterGroupFixed: NamaCommon.getStoredValue('filter-group-fixed', false),
    showIdsActive: false,
    inChangeDimsDialog: false,
    fieldToFocusOptions: {} as FieldToFocusOptions,
    resizerPosition: 0,
    isResizing: false,
    showEditUIElementProperties: false,
    fieldsHelpMessages: {} as Record<string, DTOFieldHelpMessage[]>,
    doNotFocusColumn: false,
  }),
  getters: {
    compactMode: (state) => state.densityMode === 'Compact' || state.densityMode === 'Ultra',
    isUltra: (state) => state.densityMode === 'Ultra',
  },
  actions: {
    async initAiChatIdIfNeeded() {
      if (this.aiChatId)
        return;
      this.aiChatId = await ClientCache.asyncNextGUIDId() || '';
    }, updateAIChatMessage(message: DTOAIChatMessage) {
      if (!this.aiChatMessages)
        this.aiChatMessages = [];
      this.aiChatMessages.push(message);
    }, clearChatHistory() {
      this.aiChatMessages = [];
      this.aiChatId = '';
    }, getLastAIMessage() {
      return this.aiChatMessages[this.aiChatMessages.length - 1];
    },
    activateReplicationOptions() {
      this.replicationOptionsActive = true;
    },
    activateServerLog() {
      this.serverLogActive = true;
    },
    activateShowFieldInfo() {
      this.showFieldInfoActive = true;
    },
    setResizerPosition(positionY: number) {
      this.resizerPosition = positionY
    },
    activateShowHelpMessages() {
      this.showHelpMessagesActive = true;
    },
    activateEditUIElementProperties() {
      this.showEditUIElementProperties = true;
    },
    toggleShowIds() {
      this.showIdsActive = !this.showIdsActive;
    },
    toggleShowHelpMessages() {
      this.showHelpMessagesActive = !this.showHelpMessagesActive
    },
    setFieldsHelpMessages(entityType: string, val: DTOFieldHelpMessage[]) {
      this.fieldsHelpMessages[entityType] = val;
    },
    setDoNotFocusColumn(doNotFocusColumn: boolean) {
      this.doNotFocusColumn = doNotFocusColumn;
    },
    setFieldToFocusOptions(fieldToFocusOptions: FieldToFocusOptions) {
      this.fieldToFocusOptions = fieldToFocusOptions;
    },
    onNavBarHeightChanged(height: number) {
      this.navBarHeight = height;
    }, toggleDarkMode() {
      this.updateDarkMde(!this.darkMode);
    }, updateDarkModeTransient(darkMode: boolean){
      this.updateDarkMde(darkMode,true);
    }
    , updateDarkMde(darkMode: boolean, transient?: boolean) {
      if (darkMode)
        document.documentElement.classList.add('tw-dark');
      else
        document.documentElement.classList.remove('tw-dark');
      if (!transient) {
        localStorage.setItem('color-theme', darkMode ? 'dark' : 'light');
        this.darkMode = darkMode;
      }
      setCssVar('primary', darkMode ? useThemeStore().getPrimaryDarkColor() : useThemeStore().getPrimaryColor());
      AGGridUtils.updateThemeVars()
    }, updateQuadLayout(quadLayout: boolean) {
      this.quadLayout = quadLayout;
      if (this.quadLayout)
        document.documentElement.classList.add('tw-quad');
      else
        document.documentElement.classList.remove('tw-quad');
      localStorage.setItem('quad-layout', this.quadLayout + "");
    }, updateSidebarMenuMode(sidebarMenuMode: object) {
      this.sidebarMenuMode = sidebarMenuMode;
      localStorage.setItem('side-bar-menu-mode', NamaCommon.stringify(this.sidebarMenuMode));
    }, updateDensityMode(densityMode: string) {
      this.densityMode = densityMode;
      document.documentElement.classList.remove('tw-compact', 'tw-ultra');
      if (densityMode === 'Compact')
        document.documentElement.classList.add('tw-compact');
      else if (densityMode === 'Ultra')
        document.documentElement.classList.add('tw-ultra');
      AGGridUtils.updateCompactVars();
      localStorage.setItem('density-mode', densityMode);
    }, updateCompactMode(compactMode: boolean) {
      this.updateDensityMode(compactMode ? 'Compact' : 'Normal');
    }, updateToolbarLabel(showToolbarLabels: boolean) {
      this.showToolbarLabels = showToolbarLabels;
      localStorage.setItem('show-toolbar-labels', this.showToolbarLabels + "");
    }, toggleSidebarMenuIconColors(showSidebarMenuIconColors: boolean) {
      this.showSidebarMenuIconColors = showSidebarMenuIconColors;
      localStorage.setItem('showSidebarMenuIconColors', this.showSidebarMenuIconColors + "");
    }, updateHorizontalMode(horizontalMode: boolean) {
      this.horizontalMode = horizontalMode;
      if (this.horizontalMode)
        document.documentElement.classList.add('tw-horizontal');
      else
        document.documentElement.classList.remove('tw-horizontal');
      localStorage.setItem('horizontal-mode', this.horizontalMode + "");
    }, updateSidebarFixed(sidebarFixed: boolean) {
      this.sidebarFixed = sidebarFixed;
      localStorage.setItem('sidebar-fixed', this.sidebarFixed + "");
      if (this.sidebarFixed)
        this.isSideBarOpen = true;
    },updateFilterGroupFixed(filterGroupFixed: boolean) {
      this.filterGroupFixed = filterGroupFixed;
      localStorage.setItem('filter-group-fixed', this.filterGroupFixed + "");
    }, updateGridOption<K extends (keyof GridOptions | "showTotalRowCount")>(key: K, value: any) {
      const route = router.currentRoute.value.name as RouteNames
      const currentRoute = route === RouteNames.EDIT_VIEW_ROUTE ? RouteNames.EDIT_VIEW_ROUTE : RouteNames.LIST_VIEW_ROUTE
      if (ObjectChecker.isEmptyOrNull(this.gridOptions) || ObjectChecker.isEmptyObject(this.gridOptions))
        this.gridOptions = {
          [RouteNames.EDIT_VIEW_ROUTE]: {},
          [RouteNames.LIST_VIEW_ROUTE]: {}
        };
      if (ObjectChecker.isNullOrUndefined(this.gridOptions[currentRoute]))
        this.gridOptions[currentRoute] = {};
      const currentGridOptions = this.gridOptions[currentRoute] ?? {}
      if (value === undefined)
        delete currentGridOptions[key];
      else
        currentGridOptions[key] = value;
      localStorage.setItem('gridOptions', JSON.stringify(this.gridOptions));
    }, getGridOption<K extends (keyof GridOptions | "showTotalRowCount")>(key: K): any {
      const route = router.currentRoute.value.name as RouteNames
      const currentRoute = route === RouteNames.EDIT_VIEW_ROUTE ? RouteNames.EDIT_VIEW_ROUTE : RouteNames.LIST_VIEW_ROUTE
      const currentGridOptions = this.gridOptions[currentRoute]
      return currentGridOptions?.[key];
    }, updateInChangeDimsDialog(inChangeDimsDialog: boolean) {
      this.inChangeDimsDialog = inChangeDimsDialog;
    }, updateNotificationsSound(SoundStr: string) {
      this.notificationsSound = SoundStr;
      NamaCommon.setInStorage('notifications-sound', this.notificationsSound);
    }, updateApprovalsSound(SoundStr: string) {
      this.approvalsSound = SoundStr;
      NamaCommon.setInStorage('approvals-sound', this.approvalsSound);
    }, updateMessagesSound(SoundStr: string) {
      this.messagesSound = SoundStr;
      NamaCommon.setInStorage('messages-sound', this.messagesSound);
    }, updateTaskEndedSound(SoundStr: string) {
      this.taskEndedSound = SoundStr;
      NamaCommon.setInStorage('task-ended-sound', this.taskEndedSound);
    }
  }
})

window.addEventListener('storage', (e: StorageEvent) => {
  if (e.key === 'color-theme')
    useNamaFlagsStore().updateDarkMde(e.newValue === "dark");
  else if (e.key === 'density-mode')
    useNamaFlagsStore().updateDensityMode(e.newValue || 'Normal');
  else if (e.key === 'compact-mode')
    useNamaFlagsStore().updateCompactMode(e.newValue === "true");
  else if (e.key === 'horizontal-mode')
    useNamaFlagsStore().updateHorizontalMode(e.newValue === "true");
  else if (e.key === 'quad-layout')
    useNamaFlagsStore().updateQuadLayout(e.newValue === "true");
  else if (e.key === 'sidebar-fixed')
    useNamaFlagsStore().updateSidebarFixed(e.newValue === "true");
  else if (e.key === 'showSidebarMenuIconColors')
    useNamaFlagsStore().toggleSidebarMenuIconColors(e.newValue === "true");
})

export function useDarkMode() {
  const {darkMode} = storeToRefs(useNamaFlagsStore());
  watch(() => darkMode.value, (value) => {
    useNamaFlagsStore().updateDarkMde(value);
  })
  return {darkMode};
}

export function useQuadLayout() {
  const {quadLayout} = storeToRefs(useNamaFlagsStore());
  watch(() => quadLayout.value, (value) => {
    useNamaFlagsStore().updateQuadLayout(value);
  })
  return {quadLayout};
}

export function useSideBarMenuMode() {
  const {sidebarMenuMode} = storeToRefs(useNamaFlagsStore());
  watch(() => sidebarMenuMode.value, (value) => {
    useNamaFlagsStore().updateSidebarMenuMode(value);
  })
  return {sidebarMenuMode};
}

export function useToolbarLabels() {
  const {showToolbarLabels} = storeToRefs(useNamaFlagsStore());
  watch(() => showToolbarLabels.value, (value) => {
    useNamaFlagsStore().updateToolbarLabel(value);
  })
  return {showToolbarLabels};
}

export function useSidebarMenuIconColors() {
  const {showSidebarMenuIconColors} = storeToRefs(useNamaFlagsStore());
  watch(() => showSidebarMenuIconColors.value, (value) => {
    useNamaFlagsStore().toggleSidebarMenuIconColors(value);
  })
  return {showSidebarMenuIconColors};
}

export function useCompactMode() {
  const {compactMode} = storeToRefs(useNamaFlagsStore());
  watch(() => compactMode.value, (value) => {
    useNamaFlagsStore().updateCompactMode(value);
  })
  return {compactMode};
}

export function useDensityMode() {
  const {densityMode} = storeToRefs(useNamaFlagsStore());
  watch(() => densityMode.value, (value) => {
    useNamaFlagsStore().updateDensityMode(value);
  });
  return {densityMode};
}

export function useHorizontalMode() {
  const {horizontalMode} = storeToRefs(useNamaFlagsStore());
  watch(() => horizontalMode.value, (value) => {
    useNamaFlagsStore().updateHorizontalMode(value);
  })
  return {horizontalMode};
}

export function useSidebarFixed() {
  const {sidebarFixed} = storeToRefs(useNamaFlagsStore());
  watch(() => sidebarFixed.value, (value) => {
    useNamaFlagsStore().updateSidebarFixed(value);
  })
  return {sidebarFixed};
}

export function useFilterGroupFixed() {
  const {filterGroupFixed} = storeToRefs(useNamaFlagsStore());
  watch(() => filterGroupFixed.value, (value) => {
    useNamaFlagsStore().updateFilterGroupFixed(value);
  })
  return {filterGroupFixed};
}
